import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Bitcoin, Home, Car, TrendingUp, Shield, Heart,
  Loader2, ArrowRight, Info, AlertTriangle, Check,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useLifely } from '../context/LifelyContext';
import { aiAPI } from '../services/api';
import UpgradeModal from '../components/billing/UpgradeModal';
import { toast } from 'sonner';

const MAPS = [
  {
    id: 'crypto', name: 'Crypto & Digital Assets', Icon: Bitcoin,
    placeholder: 'e.g. I have a Ledger Nano X with about 2 ETH and some Bitcoin stored in my home office safe. The seed phrase backup is on a steel plate in the filing cabinet.',
  },
  {
    id: 'property', name: 'Property', Icon: Home,
    placeholder: 'e.g. I own a 3-bedroom house at 42 Example Street, Auckland. Joint ownership with my wife Sarah. Mortgaged with ANZ, roughly worth $1.2M.',
  },
  {
    id: 'vehicles', name: 'Vehicles', Icon: Car,
    placeholder: 'e.g. 2019 Toyota Camry, silver, registration ABC123. Finance with Toyota Financial, about $15k remaining.',
  },
  {
    id: 'shares', name: 'Shares & Investments', Icon: TrendingUp,
    placeholder: 'e.g. ISA with Hargreaves Lansdown, mostly in Vanguard funds, roughly £50k. Account number HL12345.',
  },
  {
    id: 'insurance', name: 'Insurance Policies', Icon: Shield,
    placeholder: 'e.g. Life insurance with AIA, $500k coverage, policy LI-98765. Renews in March.',
  },
  {
    id: 'pets', name: 'Pets & Animals', Icon: Heart,
    placeholder: 'e.g. Border Collie named Max, 3 years old, microchip 123456789. Vet is Auckland Animal Hospital. My sister Jane would care for him.',
  },
];

const humanize = (key) =>
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();

const AIAssist = () => {
  const navigate = useNavigate();
  const { user } = useLifely();
  const canUseAI = ['standard', 'plus'].includes(user?.plan);

  const [selectedMapId, setSelectedMapId] = useState(null);
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [fields, setFields] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);

  const selectedMap = MAPS.find((m) => m.id === selectedMapId) || null;

  const handleSelectMap = (id) => {
    setSelectedMapId(id);
    setFields(null);
  };

  const handleExtract = async () => {
    if (!canUseAI) {
      setShowUpgrade(true);
      return;
    }
    if (!selectedMap || !description.trim()) return;
    setLoading(true);
    setFields(null);
    try {
      const { fields: extracted } = await aiAPI.extractMapEntry(selectedMap.id, description.trim());
      if (!extracted || Object.keys(extracted).length === 0) {
        toast.error('No fields could be extracted. Try adding more detail.');
      } else {
        setFields(extracted);
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        setShowUpgrade(true);
      } else {
        toast.error(err?.response?.data?.message || 'Could not extract fields. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const handleAddToMap = () => {
    const prefill = { ...fields };
    // The visible "Broker / Platform" field on shares is bound to the entry location.
    if (selectedMap.id === 'shares' && prefill.broker !== undefined) {
      prefill.location = prefill.broker;
      delete prefill.broker;
    }
    navigate(`/maps/${selectedMap.id}`, { state: { aiPrefill: prefill } });
  };

  const reset = () => {
    setFields(null);
    setDescription('');
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fade-in" data-testid="ai-assist-page">
      {/* Header */}
      <section className="space-y-2 border-b border-border pb-6">
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-foreground flex items-center gap-2">
          <Sparkles className="w-7 h-7 text-primary" />
          AI Map Assistant
        </h1>
        <p className="text-sm text-muted-foreground">
          Describe an asset in plain English and we&apos;ll fill in the details for you.
        </p>
      </section>

      {/* Safety banner */}
      <div className="flex gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-800 dark:text-amber-200">
          AI assists with structure - always review before saving. Never describe passwords,
          seed phrases, or private keys.
        </p>
      </div>

      {/* Plan gate banner */}
      {!canUseAI && (
        <div className="flex items-center justify-between gap-3 p-4 rounded-lg bg-muted/60 border border-border">
          <div className="flex items-center gap-3 text-sm">
            <Sparkles className="w-5 h-5 text-primary flex-shrink-0" />
            <span>AI features are available on the Standard plan.</span>
          </div>
          <Button size="sm" onClick={() => setShowUpgrade(true)} data-testid="ai-upgrade-btn">Upgrade</Button>
        </div>
      )}

      {/* Step 1 — map type */}
      <section className="space-y-3">
        <p className="text-sm font-medium text-foreground">1. What are you documenting?</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {MAPS.map(({ id, name, Icon }) => {
            const selected = id === selectedMapId;
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleSelectMap(id)}
                data-testid={`ai-map-${id}`}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-center transition-colors ${
                  selected ? 'border-primary bg-primary/5 text-foreground' : 'border-border hover:bg-muted/40 text-muted-foreground'
                }`}
              >
                <Icon className={`w-6 h-6 ${selected ? 'text-primary' : ''}`} />
                <span className="text-xs font-medium leading-tight">{name}</span>
              </button>
            );
          })}
        </div>
      </section>

      {/* Step 2 — description */}
      {selectedMap && (
        <section className="space-y-3">
          <p className="text-sm font-medium text-foreground">2. Describe it</p>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={selectedMap.placeholder}
            className="min-h-[112px] resize-none"
            data-testid="ai-description-input"
          />
          <Button
            className="w-full"
            onClick={handleExtract}
            disabled={loading || (canUseAI && !description.trim())}
            data-testid="ai-extract-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Extract Fields
          </Button>
        </section>
      )}

      {/* Step 3 — results */}
      {fields && selectedMap && (
        <section className="space-y-3">
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Check className="w-4 h-4 text-primary" />
            3. Review &amp; edit
          </div>
          <Card className="border border-border">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Tweak anything below before adding it to your map.</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(fields).map(([key, value]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{humanize(key)}</Label>
                    {typeof value === 'boolean' ? (
                      <div className="flex items-center gap-2 h-9">
                        <Checkbox checked={value} onCheckedChange={(v) => updateField(key, !!v)} />
                        <span className="text-sm">{value ? 'Yes' : 'No'}</span>
                      </div>
                    ) : (
                      <Input
                        value={value == null ? '' : String(value)}
                        onChange={(e) => updateField(key, e.target.value)}
                        data-testid={`ai-field-${key}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
                <Button className="w-full sm:w-auto" onClick={handleAddToMap} data-testid="ai-add-to-map-btn">
                  Add to {selectedMap.name}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <button
                  type="button"
                  onClick={reset}
                  className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                  data-testid="ai-try-again"
                >
                  Try again
                </button>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      <UpgradeModal isOpen={showUpgrade} onClose={() => setShowUpgrade(false)} trigger="ai" />
    </div>
  );
};

export default AIAssist;
